import { type FC, useCallback, useEffect, useRef } from 'react'

import { useUnit } from 'effector-react'

import { DropdownMenuItem } from '@/components/ui/dropdown-menu.tsx'
import { deleteMessageFx } from '@/models/chattingUI/messages/messages.ts'
import type { Message } from '@/modules/chatting/types.ts'

interface Props {
  message: Message
}

export const UnsendDropdownItem: FC<Props> = ({ message }) => {
  const isDeleteMessageLoading = useUnit(deleteMessageFx.pending)
  const showTime = message.price === 0 && message.deletableUntil

  const getButtonText = useCallback(() => {
    const difference = showTime
      ? Date.parse(message.deletableUntil!) - Date.now()
      : 0

    if (difference <= 0 || difference > 1000 * 60 * 60 * 24) {
      return 'Unsend'
    }

    const hours = Math.floor(
      (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
    )
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60))
    const seconds = Math.floor((difference % (1000 * 60)) / 1000)
    let timeString = ''

    if (hours > 0) {
      timeString += ' ' + hours + 'h'
    }

    timeString += ' ' + minutes + 'm'

    if (hours === 0) {
      timeString += ' ' + seconds + 's'
    }

    return 'Unsend' + timeString
  }, [showTime, message.deletableUntil])

  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const interval = setInterval(() => {
      const current = ref.current

      if (current) {
        current.innerText = getButtonText()
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [getButtonText])

  const handleUnsend = useCallback(async () => {
    await deleteMessageFx(message.id)
  }, [message])

  if (
    !message.deletableUntil ||
    Date.parse(message.deletableUntil) <= Date.now() ||
    (message.price > 0 && message.isOpened)
  ) {
    return
  }

  return (
    <DropdownMenuItem
      ref={ref}
      disabled={isDeleteMessageLoading}
      onClick={handleUnsend}
    >
      {getButtonText()}
    </DropdownMenuItem>
  )
}
